import { Helmet } from "react-helmet-async";
import { useEffect, useState, useCallback, useRef } from "react";
import {
  Card,
  Table,
  Stack,
  Paper,
  Button,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Container,
  Typography,
  TableContainer,
  TableHead,
  CircularProgress,
  Box,
  FormControl,
  InputLabel,
  Select,
  IconButton,
  Popover,
  MenuList,
  TextField,
  Grid,
  Alert,
  Snackbar,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import Iconify from "../components/iconify";
import Scrollbar from "../components/scrollbar";
import MatchDetailsDialog from "../components/MatchDetailsDialog";
import { useNavigate } from "react-router-dom";
import { get, post } from "src/utils/networkUtils";
import { fDateTime } from "src/utils/formatTime";
import { useDispatch } from "react-redux";
import { setFixtures, setSelectedMatch } from "src/store/slices/fixturesSlice";
import useWithUrlTenant from "src/hooks/useTenant";
import useTenantCompetitions from "src/hooks/useTenantCompetitions";

const getDefaultStartDate = () => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
};

const getDefaultEndDate = () => {
  const endDate = new Date();
  endDate.setDate(endDate.getDate() + 45);
  endDate.setHours(23, 59, 59, 999);
  return endDate;
};
const getMinutes = (row) => {
  if (row.status !== "LIVE") {
    return "";
  }
  return row.details && row.details.Minute ? ` (${row.details.Minute}')` : "--";
};

export default function FixturesListPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState(null);
  const [showMatch, setShowMatch] = useState(null);
  const [competitionId, setCompetitionId] = useState(null);
  const [competitionSport, setCompetitionSport] = useState(null);
  const previousCompetitionIdRef = useRef(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRowIndex, setSelectedRowIndex] = useState(null);
  const [startDate, setStartDate] = useState(getDefaultStartDate());
  const [endDate, setEndDate] = useState(getDefaultEndDate());
  const [scoringInProgress, setScoringInProgress] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const dispatch = useDispatch();
  const withTenant = useWithUrlTenant();

  // Use the new hook for tenant competitions
  const { tenantCompetitions } = useTenantCompetitions();

  // Reset rows and set competition when tenant changes
  useEffect(() => {
    setRows(null);
    previousCompetitionIdRef.current = null;
    if (tenantCompetitions.length > 0) {
      setCompetitionId(tenantCompetitions[0].competitionId);
      setCompetitionSport(tenantCompetitions[0].sport);
    } else {
      setCompetitionId(null);
    }
  }, [tenantCompetitions]);

  const handleMenuOpen = (event, rowIndex) => {
    setAnchorEl(event.currentTarget);
    setSelectedRowIndex(rowIndex);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedRowIndex(null);
  };

  const handleMenuAction = (action, row) => {
    handleMenuClose();

    switch (action) {
      case "viewStats":
        setShowMatch({ ...row });
        break;
      case "forceScoring":
        handleFixScoring(row.externalId);
        break;
      case "viewFeeds":
        navigate(`/dashboard/fixture/feeds?external_id=${row.externalId}`);
        break;
      case "liveScoring":
        dispatch(setSelectedMatch(row));
        navigate(withTenant("/dashboard/fixtures/match-live-scoring"));
        break;
      default:
        break;
    }
  };

  const fetchFixtures = useCallback(async () => {
    if (!competitionId) {
      console.log("No competitionId selected, skipping fixtures fetch");
      return;
    }

    console.log(`Fetching fixtures for competitionId: ${competitionId}`);
    previousCompetitionIdRef.current = competitionId;
    setLoading(true);

    try {
      const response = await get(
        withTenant(
          `/admin/fixtures?competition_id=${competitionId}&start_date=${startDate?.toISOString()}&end_date=${endDate?.toISOString()}`
        )
      );
      setRows(response.fixtures);
      dispatch(setFixtures(response.fixtures));
      console.log(`Successfully fetched ${response.fixtures?.length || 0} fixtures`);
    } catch (error) {
      console.error("Error fetching fixtures:", error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [competitionId, startDate, endDate, dispatch, withTenant]);


  useEffect(() => {
    fetchFixtures();
  }, [fetchFixtures]);

  const handleCompetitionIdChange = (value) => {
    console.log(`Competition changed to: ${value}`);
    setCompetitionId(value);
    // fetchFixtures will be called automatically via useEffect when competitionId changes
  };

  const handleFixScoring = async (fixtureId) => {
    setScoringInProgress(true);
    try {
      await post(`/admin/fixtures/${fixtureId}/score`);
      setSuccessMessage(`Fixture ${fixtureId} scored successfully!`);
    } catch (error) {
      console.error("Error scoring fixture:", error);
      setSuccessMessage(`Error scoring fixture ${fixtureId}`);
    } finally {
      setScoringInProgress(false);
    }
  };

  const handleCloseSuccessMessage = () => {
    setSuccessMessage("");
  };

  const scoringStartDate = new Date();
  scoringStartDate.setHours(scoringStartDate.getHours() - 3);
  const scoringEndDate = new Date();
  scoringEndDate.setHours(scoringEndDate.getHours() + 3);
  console.log("------ competitionSport ---------", competitionSport);
  return (
    <>
      <Helmet>
        <title> Fixtures </title>
      </Helmet>

      <Container>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={3}
        >
          <Typography variant="h4" gutterBottom sx={{ mr: 2 }}>
            Fixtures
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(newValue) => setStartDate(newValue)}
                renderInput={(params) => <TextField {...params} size="small" />}
                slotProps={{ textField: { size: "small" } }}
              />
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(newValue) => setEndDate(newValue)}
                renderInput={(params) => <TextField {...params} size="small" />}
                slotProps={{ textField: { size: "small" } }}
              />
            </LocalizationProvider>
            <FormControl sx={{ minWidth: 300 }} size="small">
              <InputLabel id="competition-select-label">Competition</InputLabel>
              <Select
                labelId="competition-select-label"
                id="competition-select"
                value={competitionId || ""}
                label="Competition"
                onChange={(event) =>
                  handleCompetitionIdChange(event.target.value)
                }
              >
                {tenantCompetitions.map((competition, index) => (
                  <MenuItem
                    value={competition.competitionId}
                    key={`competition-${competition.competitionId}`}
                  >
                    {competition.competitionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:list-fill" />}
              onClick={() => navigate("/dashboard/basic-feeds")}
              sx={{ whiteSpace: "nowrap" }}
            >
              Squad/Schedule Feeds
            </Button>
            <Button
              variant="contained"
              startIcon={<Iconify icon="eva:plus-fill" />}
              onClick={() => navigate(withTenant("/dashboard/fixtures/add-match"))}
              sx={{ whiteSpace: "nowrap" }}
            >
              Add Match
            </Button>
          </Stack>
        </Stack>

        <Card>
          {rows === null || loading ? (
            <Box
              sx={{ display: "flex", justifyContent: "center" }}
              style={{ padding: 20 }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Scrollbar>
              <TableContainer component={Paper}>
                <Table sx={{ minWidth: 650 }} aria-label="simple table">
                  <TableHead>
                    <TableRow>
                      <TableCell>ID</TableCell>
                      <TableCell>Fixture ID</TableCell>
                      <TableCell>Match Day</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Scoring</TableCell>
                      <TableCell>Date UTC</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, index) => (
                      <TableRow
                        key={index}
                        sx={{
                          "&:last-child td, &:last-child th": { border: 0 },
                        }}
                      >
                        <TableCell component="th" scope="row">
                          {row.fixtureId}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {row.externalId}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {row.details && row.details.MatchDay
                            ? row.details.MatchDay
                            : "N/A"}
                        </TableCell>
                        <TableCell component="th" scope="row" sx={{ maxWidth: 200 }}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={12}>{row.details.HomeTeamName}</Grid>
                            <Grid item xs={12}>{`${row.details.HomeTeamScore}:${row.details.AwayTeamScore}`}</Grid>
                            <Grid item xs={12}>{row.details.AwayTeamName}</Grid>
                          </Grid>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {row.status} {getMinutes(row)}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {new Date(row.startDateUtc) > scoringStartDate &&
                            new Date(row.startDateUtc) < scoringEndDate ? (
                            <>
                              <Iconify
                                icon={"eva:checkmark-circle-2-outline"}
                                sx={{ color: "green" }}
                              />{" "}
                              In Progress
                            </>
                          ) : (
                            <Iconify
                              icon={"eva:close-circle-outline"}
                              sx={{ color: "red" }}
                            />
                          )}
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {fDateTime(new Date(row.startDateUtc))}
                        </TableCell>
                        <TableCell align="right">
                          <IconButton
                            onClick={(event) => handleMenuOpen(event, index)}
                            size="small"
                          >
                            <Iconify icon="eva:more-vertical-fill" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Scrollbar>
          )}
        </Card>
      </Container>

      <Snackbar
        open={Boolean(successMessage)}
        autoHideDuration={6000}
        onClose={handleCloseSuccessMessage}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        sx={{
          position: "fixed",
          top: 16,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 9999,
        }}
      >
        <Alert
          onClose={handleCloseSuccessMessage}
          severity="success"
          sx={{ width: "100%", minWidth: "300px" }}
        >
          {successMessage}
        </Alert>
      </Snackbar>

      <MatchDetailsDialog
        sportType={competitionSport}
        open={showMatch !== null}
        onClose={() => setShowMatch(null)}
        matchData={{ showMatch }}
      />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuList>
          <MenuItem
            onClick={() =>
              handleMenuAction("liveScoring", rows[selectedRowIndex])
            }
            sx={{ minWidth: 140 }}
          >
            <Iconify icon="eva:activity-fill" sx={{ mr: 1 }} />
            Match Live Scoring
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleMenuAction("viewStats", rows[selectedRowIndex])
            }
            sx={{ minWidth: 140 }}
          >
            <Iconify icon="eva:eye-fill" sx={{ mr: 1 }} />
            View Stats
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleMenuAction("forceScoring", rows[selectedRowIndex])
            }
            sx={{ minWidth: 140 }}
            disabled={scoringInProgress}
          >
            {scoringInProgress ? (
              <CircularProgress size={16} sx={{ mr: 1 }} />
            ) : (
              <Iconify icon="eva:flash-fill" sx={{ mr: 1 }} />
            )}
            Force Scoring
          </MenuItem>
          <MenuItem
            onClick={() =>
              handleMenuAction("viewFeeds", rows[selectedRowIndex])
            }
            sx={{ minWidth: 140 }}
          >
            <Iconify icon="eva:list-fill" sx={{ mr: 1 }} />
            View Feeds
          </MenuItem>
        </MenuList>
      </Popover>
    </>
  );
}
